/**
 * The product page's review cards, and its question cards, as a print row
 * (sections/reviews-paper.liquid says why they are not in the engine's own
 * row: Judge.me's widget owns them).
 *
 * The cards stay where the widget put them, in its list, which the
 * stylesheet lays out as a row that scrolls sideways. What this script adds
 * is what only the engine can say, and what the widget would undo: each
 * card's seeded place and lean in the row, from the engine's own
 * (PrintRow.tileStyle in assets/print-row.js), as custom properties on the
 * card (--rot, --drop, --shift, --z), keyed by the review's own id so a card
 * leans the same way whatever the sort or page, and its number on the pad
 * (--card-no). The widget re-renders its list in place when the shopper
 * sorts, searches, loads more or changes tab, reusing some cards and
 * replacing the rest, so every change under it puts the places back, and a
 * card the widget has just made gets its own.
 *
 * Under a mouse the cards near the cursor move as a row's prints do: the
 * engine's own cursor field (PrintRow.magnet), attached to each list the
 * widget makes, over the cards in it, at a fraction of its strength (a card
 * is a page a thousand pixels tall, not a print). It is told how the
 * stylesheet rests each card, so it starts from that pose and hands the card
 * back to it, and told again whenever the cards change. It has nothing to do
 * with a click, which reaches the widget as it always has, and a touch screen
 * or a reader who asks for reduced motion gets none of it, as for a row.
 *
 * A customer's same words under several products (one review pasted under
 * each, which the widget shows as a card apiece, at times under two forms
 * of their name, "Kris F." and "k.f.", the same day) are one card in the row:
 * the first, the others marked data-repeat, which the stylesheet hides and
 * the numbers, the field and the paging pass over, as the home page's row
 * drops them from its feeds (assets/print-carousel-reviews.js).
 *
 * The widget pages its reviews with one button, "Show more reviews", below
 * the row. As the row's last card comes into view the script presses that
 * button for the shopper (a request for the next cards, as pressing it
 * would be), so the row carries on past its end as a print row does; the
 * button stays for anyone who would rather press it. Only one press is made
 * at a time, and none while the last card is out of view.
 *
 * A product with no reviews yet gets only the open tab's button from the
 * widget; the script adds the other tab's beside it, so Write a review and
 * Ask a question are both beside the tabs, as on any other product.
 *
 * Fails quietly: nothing here throws into the page.
 */
(() => {
  const CARDS = ".jdgm-review-list > .jm-review-item, .jdgm-question-item";
  const PAGING = ".jm-load-more-pagination button";
  // How long a press of the widget's button is given to bring its cards.
  const PAGING_WAIT = 2500;
  // The empty state's buttons, and the tab each opens a form from, with its
  // words in the widget's settings and its kind in the header.
  const EMPTY = ".jm-no-reviews-state__action-buttons";
  const ACTIONS = [
    {
      tab: "jdgm-tab_product_reviews",
      text: "widget_open_form_text",
      kind: "primary"
    },
    {
      tab: "jdgm-tab_questions_answers",
      text: "widget_open_question_form_text",
      kind: "secondary"
    }
  ];

  // What a card is known by: a review has an id of its own; a question has
  // only who asked and when, in its label.
  const keyOf = (card) =>
    card.getAttribute("review-uuid") ||
    card.getAttribute("aria-label") ||
    card.textContent.trim().slice(0, 80);

  // The cards, by the list each is in (a review tab's, a question tab's).
  const listsOf = (widget) => {
    const lists = new Map();
    widget.querySelectorAll(CARDS).forEach((card) => {
      lists.set(card.parentElement, [
        ...(lists.get(card.parentElement) || []),
        card
      ]);
    });
    return lists;
  };

  // A review's words, to tell a repeat by: its title and its text, in
  // lower case, without punctuation (assets/print-carousel-reviews.js's).
  const words = (card) =>
    ["title", "body"]
      .map(
        (part) =>
          card.querySelector(`.jm-review-content__${part}`)?.textContent ?? ""
      )
      .join(" ")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  // The same words, or nearly (four in five shared): a review pasted under
  // another product, perhaps touched up.
  const similar = (first, second) => {
    if (first === second) return true;
    const left = new Set(first.split(" "));
    const right = new Set(second.split(" "));
    const shared = [...left].filter((word) => right.has(word)).length;
    return shared / (left.size + right.size - shared) >= 0.8;
  };

  // How many words a review needs before the same words on the same day
  // are taken for the same review under another name: fewer ("Love it!")
  // are said by many.
  const SAME_DAY_WORDS = 8;

  // A list's cards less its repeats: the first of each review is kept, and
  // a later card with its words (or nearly) from the same customer, or on
  // the same day, is marked (and a card the widget reuses is unmarked when
  // it is no longer one). A question has no reviewer, and Anonymous is no
  // one customer.
  const withoutRepeats = (cards) => {
    const kept = [];
    return cards.filter((card) => {
      const who = (
        card.querySelector(".jm-reviewer-info__name")?.textContent ?? ""
      )
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
      const day =
        card
          .querySelector(".jm-reviewer-info__details")
          ?.textContent.match(/\d\d\/\d\d\/\d{4}/)?.[0] ?? "";
      const said = words(card);
      const sameDay = Boolean(day) && said.split(" ").length >= SAME_DAY_WORDS;
      const repeat =
        Boolean(who && said) &&
        kept.some(
          (other) =>
            ((who !== "anonymous" && other.who === who) ||
              (sameDay && other.day === day)) &&
            similar(other.said, said)
        );
      card.toggleAttribute("data-repeat", repeat);
      if (!repeat) kept.push({ who, day, said });
      return !repeat;
    });
  };

  // A card's place and lean, and its number on the pad: its place in the
  // list, from 01 (the stylesheet's counter would say the same, but Safari
  // counts none of the cards).
  const place = (card, index) => {
    const style = {
      ...window.PrintRow.tileStyle(`${keyOf(card)}#0`, index),
      "--card-no": `"${String(index + 1).padStart(2, "0")}"`
    };
    Object.entries(style).forEach(([name, value]) => {
      if (card.style.getPropertyValue(name) === value) return;
      card.style.setProperty(name, value);
    });
  };

  // The cards directly in a list that show, which the cursor field moves.
  const IN_LIST =
    ":scope > .jm-review-item:not([data-repeat]), :scope > .jdgm-question-item";
  // What the field does to a card, against what it does to a print (the
  // names are the engine's, in px and degrees; assets/print-row.js): rise,
  // grow, lean toward the pointer, tip in 3D, and what the cards beside it
  // do (sway, tip away). A card is a thousand pixels tall, so each is a
  // small share of what moves a print.
  const TUNE = {
    lift: 8,
    grow: 0.015,
    pull: 0.02,
    tilt: 3,
    sway: 6,
    lean: 0.8
  };

  // How the stylesheet rests a card, in pixels and degrees: its seeded
  // place and lean, less what the row's tokens take off them.
  const poseOf = (card) => {
    const tokens = getComputedStyle(card.parentElement);
    const own = (name) => parseFloat(card.style.getPropertyValue(name)) || 0;
    const token = (name) => parseFloat(tokens.getPropertyValue(name)) || 0;
    return {
      shift: own("--shift") * token("--row-shift"),
      drop: own("--drop") * token("--row-drop"),
      rot: own("--rot") * token("--row-lean")
    };
  };

  // Attaches the cursor field to a list once: the list's scroller is the
  // widget's body around it. Nothing, without a mouse.
  const attached = new WeakSet();
  const attach = (list) => {
    if (attached.has(list)) return;
    attached.add(list);
    const scroller = list.closest(".jm-review-widget__body");
    if (!scroller) return;
    window.PrintRow.magnet?.(scroller, list, {
      slots: IN_LIST,
      pose: poseOf,
      tune: TUNE
    });
  };

  // Presses the widget's "Show more" button when the row's end is on screen.
  const pager = (widget) => {
    const visible = new Set();
    let busy = false;
    const more = () => {
      const button = widget.querySelector(PAGING);
      if (!visible.size || busy || !button || button.disabled) return;
      busy = true;
      button.click();
      setTimeout(() => {
        busy = false;
        more();
      }, PAGING_WAIT);
    };
    const ends = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      });
      more();
    });
    const watched = new Set();
    const follow = (lists) => {
      const last = new Set([...lists.values()].map((cards) => cards.at(-1)));
      [...watched]
        .filter((card) => !last.has(card))
        .forEach((card) => {
          ends.unobserve(card);
          watched.delete(card);
          visible.delete(card);
        });
      last.forEach((card) => {
        if (watched.has(card)) return;
        watched.add(card);
        ends.observe(card);
      });
    };
    return { follow };
  };

  // Is this tab of the widget's the open one?
  const opened = (widget, action) =>
    widget.querySelector(`#${action.tab}`)?.getAttribute("aria-selected") ===
    "true";

  // Presses the tab's own button (its empty state's, or the header's if the
  // tab has cards), once the widget has switched to the tab and drawn it
  // (its words, not the tab's state, say it has: the tab is marked open
  // before the button is redrawn). A second press while one waits replaces
  // it, so the widget's button is pressed once.
  let pressing = 0;
  const pressIn = (widget, action, tries = 40) => {
    clearTimeout(pressing);
    const text = window.jdgmSettings?.[action.text]?.trim();
    const button = [
      ...widget.querySelectorAll(
        `:is(${EMPTY}, .jm-action-buttons) button:not([data-companion])`
      )
    ].find((candidate) => candidate.textContent.trim() === text);
    if (button) {
      button.click();
      return;
    }
    if (tries > 0)
      pressing = setTimeout(() => pressIn(widget, action, tries - 1), 50);
  };

  // A product with no reviews has no buttons beside the tabs, only the
  // empty state's, and that only for the open tab (Write a review under
  // Reviews, Ask a question under Questions). The other tab's is added
  // beside it, in the widget's own words, so both are there whichever tab
  // is open, as on any other product, Write a review first and the primary
  // one, as there; pressed, it opens its tab and presses the widget's own
  // button in it.
  const companion = (widget) => {
    const stack = widget.querySelector(`${EMPTY} > .jm-stack`);
    if (
      !stack ||
      widget.querySelector(".jm-review-widget-header__main-content")
    )
      return;
    const native = stack.querySelector("button:not([data-companion])");
    const open = ACTIONS.find((action) => opened(widget, action));
    const other = ACTIONS.find((action) => action !== open);
    const text = window.jdgmSettings?.[other?.text];
    if (!native || !open || !text) return;
    native.classList.replace(
      `jm-button--${other.kind}`,
      `jm-button--${open.kind}`
    );
    stack
      .querySelectorAll(`[data-companion]:not([data-companion="${other.tab}"])`)
      .forEach((button) => button.remove());
    if (stack.querySelector("[data-companion]")) return;
    const button = native.cloneNode(false);
    ["id", "data-testid"].forEach((name) => button.removeAttribute(name));
    button.classList.replace(
      `jm-button--${open.kind}`,
      `jm-button--${other.kind}`
    );
    button.dataset.companion = other.tab;
    button.textContent = text;
    button.addEventListener("click", () => {
      widget.querySelector(`#${other.tab}`)?.click();
      pressIn(widget, other);
    });
    if (ACTIONS.indexOf(other) === 0) native.before(button);
    else native.after(button);
  };

  const start = () => {
    const widget = document.getElementById("judgeme_product_reviews");
    if (!widget || !window.PrintRow?.tileStyle) return;
    const paging = pager(widget);
    let queued = false;
    const refresh = () => {
      queued = false;
      try {
        const lists = new Map(
          [...listsOf(widget)].map(([list, cards]) => [
            list,
            withoutRepeats(cards)
          ])
        );
        lists.forEach((cards, list) => {
          cards.forEach(place);
          attach(list);
          // The field reads the cards again, at the places they now have.
          list.dispatchEvent(new CustomEvent("print-row:tiles"));
        });
        paging.follow(lists);
        companion(widget);
      } catch (error) {
        console.error("[reviews-row] could not lay out the cards:", error);
      }
    };
    const changes = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(refresh);
    });
    changes.observe(widget, { childList: true, subtree: true });
    refresh();
  };

  if (window.PrintRow) start();
  else document.addEventListener("print-row:ready", start, { once: true });
})();
